"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import React, { useCallback, useEffect, useState } from "react"

import ArrowCarousel from "../../../../public/icons/arrow-up.svg"
import FallbackImage from "../../../../public/video-covers/videocarouselFallback.png"
import Stat from "../../patterns/Stat"
import Button from "../Button"
import VideoPlayer from "../VideoPlayer"

interface customerCarouselProps {
  data: any
}

const CustomersCarousel: React.FC<customerCarouselProps> = ({ data }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setSelectedIndex(api.selectedScrollSnap())
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  if (!data || data.length === 0) return null

  return (
    <>
      {/* Embla viewport */}
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {data?.map(({ title, slug, heroTitle, statRow, video }) => (
            <div key={title} className="embla__slide">
              <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12">
                <div className="col-span-2 p-4 lg:col-span-5">
                  <h2 className="mb-6 heading3 md:heading2 xl:heading1 xl:mb-12 lg:mt-4">
                    {heroTitle}
                  </h2>
                  {statRow?.map(
                    (stat, indx) =>
                      indx === 0 && (
                        <Stat
                          number={stat?.key}
                          description={stat?.value}
                          key={stat?.id}
                        />
                      ),
                  )}
                  <Button
                    to={`/customer-case-study/${slug}`}
                    variant="secondary"
                    theme="light"
                    className="mt-6"
                  >
                    Case Study
                  </Button>
                </div>
                <div className="col-span-2 lg:col-span-7">
                  {video ? (
                    <VideoPlayer
                      videoURL={video?.videoLink || undefined}
                      wistiaVideo={video?.wistiaVideo}
                      title={video?.videoName}
                      image={video?.videoCover?.url}
                    />
                  ) : (
                    video?.videoCover?.url && (
                      <Image
                        src={video?.videoCover?.url || FallbackImage}
                        alt={`${title || "Case study"} image`}
                        className="w-full max-w-full"
                        width={704}
                        height={400}
                        sizes="(min-width: 704px) 704px, 100vw"
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop logo navigation */}
      <div className="min-w-full md:mt-12 px-4">
        <div className="flex justify-between items-center">
          {data?.map(({ title, colorLogo }, index) => (
            <button
              key={title}
              onClick={() => scrollTo(index)}
              className={clsx(
                "flex-1 md:pb-6 customer-carousel-button md:h-16 items-center text-center justify-center opacity-40",
                selectedIndex === index &&
                  "customer-carousel-button-active !opacity-100",
              )}
              type="button"
              aria-label={`Go to ${title} slide`}
            >
              <div className="!hidden max-h-8 relative h-12 p-4 md:!flex items-center justify-center">
                <Image
                  src={colorLogo?.url}
                  alt={`${title} logo`}
                  sizes="(min-width: 148px) 148px, 100vw"
                  width={148}
                  height={30}
                  className="object-contain max-h-[30px]"
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="relative mb-8">
        <div className="flex items-center justify-center md:!hidden max-w-full px-4">
          {data?.map(({ title, colorLogo }, index) => (
            <div className="flex mt-4 w-full max-w-full" key={title}>
              <div
                className={clsx(
                  "max-h-6",
                  selectedIndex !== index ? "!hidden" : "!flex",
                )}
              >
                <Image
                  src={colorLogo?.url}
                  alt={`${`${title} ` || ""}logo`}
                  sizes="(min-width: 148px) 148px, 100vw"
                  width={148}
                  height={24}
                  className="object-contain max-h-6"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="absolute top-0 flex justify-between w-full mt-1 md:hidden">
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="p-4 pl-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
            type="button"
            aria-label="Previous slide"
          >
            <ArrowCarousel className="w-4 h-4 mr-10 transform -rotate-90 text-gray-90" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="p-4 pr-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
            type="button"
            aria-label="Next slide"
          >
            <ArrowCarousel className="w-4 h-4 rotate-90 text-gray-90" />
          </button>
        </div>
      </div>
    </>
  )
}

export default CustomersCarousel
